'use client';
import { type Brand } from '@curology/ui-components-web/brand';
import { Progress } from '@curology/ui-components-web/react/Progress';
import React, { useEffect } from 'react';

import { useFlashMessages } from '@/atoms/flashMessages/useFlashMessages';
import { type ExpressCurologySignUpSkinPdpSourceConfigType } from '@/components/flow/expressCurologySignUpSkin/types';
import { useStripeClient } from '@/components/payments/hooks/useStripeClient';
import { type TreatmentCategory } from '@/consts/treatmentCategories';
import { useSessionExpired } from '@/hooks/useSessionExpired';
import { AnalyticsEvent, FlowTypes, trackEvent } from '@/utils/analytics';

import { useNavigation } from '../../../hooks/useNavigation';
import {
  applePayButtonStyleConfig,
  getPaymentRequestOptions,
  handlePaymentRequestButtonOnToken,
} from '../utils/applePayUtils';

// This component is primarily based off the code in apps/curology-sign-up/src/components/product-selection/components/CheckoutButton/components/applePayButton.tsx.
export const ApplePayButton = ({
  brand,
  bundleConfig,
  disableForms,
  enableForms,
  isSubmitting,
  selectedAddOnProductItemIds,
  treatmentCategory,
  uuid,
}: {
  brand: Brand;
  bundleConfig: ExpressCurologySignUpSkinPdpSourceConfigType;
  disableForms: () => void;
  enableForms: () => void;
  isSubmitting: boolean;
  selectedAddOnProductItemIds: number[];
  treatmentCategory: TreatmentCategory;
  uuid: string;
}) => {
  const stripeClient = useStripeClient();

  const flashMessages = useFlashMessages();
  const { handleIfSessionExpired } = useSessionExpired(brand);
  const { onNext } = useNavigation();

  const applePayButtonContainerId = 'apple-pay-button-container';
  const applePayButtonId = 'apple-pay-button';

  useEffect(() => {
    if (!stripeClient) {
      return;
    }

    const renderApplePayButton = async () => {
      const paymentRequestOptions = getPaymentRequestOptions(
        bundleConfig,
        selectedAddOnProductItemIds,
      );

      const paymentRequest = stripeClient.paymentRequest(paymentRequestOptions);

      const canMakePaymentResponse = await paymentRequest.canMakePayment();

      if (canMakePaymentResponse?.applePay) {
        trackEvent(AnalyticsEvent.APPLE_PAY_SHOWN, {
          flow: FlowTypes.PDP_CHECKOUT,
        });

        const paymentRequestButton = stripeClient
          .elements()
          .create('paymentRequestButton', {
            paymentRequest,
            style: {
              paymentRequestButton: applePayButtonStyleConfig,
            },
          });

        if (paymentRequestButton) {
          paymentRequest.on('cancel', () => {
            trackEvent(AnalyticsEvent.APPLE_PAY_CANCELED, {
              flow: FlowTypes.PDP_CHECKOUT,
            });
          });

          paymentRequestButton.on('click', () => {
            trackEvent(AnalyticsEvent.APPLE_PAY_CLICKED, {
              flow: FlowTypes.PDP_CHECKOUT,
            });
          });

          paymentRequest.on('token', (event) => {
            handlePaymentRequestButtonOnToken(
              event,
              brand,
              bundleConfig,
              disableForms,
              enableForms,
              isSubmitting,
              selectedAddOnProductItemIds,
              treatmentCategory,
              uuid,
              flashMessages,
              onNext,
              handleIfSessionExpired,
            );
          });

          paymentRequestButton.mount(`#${applePayButtonId}`);
        }
      }
    };

    renderApplePayButton();
  }, [
    brand,
    bundleConfig,
    disableForms,
    enableForms,
    isSubmitting,
    selectedAddOnProductItemIds,
    treatmentCategory,
    uuid,
    stripeClient,
    flashMessages,
    handleIfSessionExpired,
    onNext,
  ]);

  return (
    <div className="cur-mt-4 cur-text-center" id={applePayButtonContainerId}>
      {!stripeClient && (
        <Progress
          className="cur-inline-block"
          progressType="circle"
          state="highlight"
        />
      )}
      {stripeClient && <div id={applePayButtonId} />}
    </div>
  );
};
